import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import api from 'services/api';

import MerchantCard from 'components/merchants/MerchantCard';
import ListItem from 'components/archives/ListItem';
import LoadMoreButton from 'components/common/LoadMoreButton';
import MapCta from 'components/common/MapCta';
import LiensPratiques from 'components/common/LiensPratiques';
import ArchivesHeader from 'components/archives/ArchivesHeader';
import MerchantsArchiveFilter from 'components/merchants/MerchantsArchiveFilter';
import ToggleView from 'components/archives/ToggleView';
import ArchivesGridView from 'components/archives/ArchivesGridView';
import ArchivesListView from 'components/archives/ArchivesListView';

import { useSubSite } from 'contexts/SubSiteContext';
import useDesignSettingsData from 'hooks/useDesignSettingsData';
import { Helmet } from 'react-helmet-async';
import Spinner from 'components/common/Spinner';
import styles from 'styles/modules/Archives.module.css';

const MerchantsPage = () => {
  const [merchants, setMerchants] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [availableTypes, setAvailableTypes] = useState([]);

  // Filtrage
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTypes, setSelectedTypes] = useState([]);

  // Pagination
  const [displayCount, setDisplayCount] = useState(9);

  // Vue
  const [view, setView] = useState('grid');

  // Loading
  const [loading, setLoading] = useState(true);

  const { isSubSite, subSiteSlug } = useSubSite();
  const { designSettings, settingsLoading, error } = useDesignSettingsData();
  const location = useLocation();

  const merchantsURL = isSubSite ? `/${subSiteSlug}/commercants` : '/commercants';

  // 1) Charger les commerçants et les types
  useEffect(() => {
    const fetchData = async () => {
      try {
        if (isSubSite) {
          const [merchantsResponse, typesResponse] = await Promise.all([
            api.get(`/subsites/${subSiteSlug}/merchants`),
            api.get('/merchant-types')
          ]);
          setMerchants(merchantsResponse.data);
          setAvailableTypes(typesResponse.data);
        } else {
          const [merchantsResponse, typesResponse] = await Promise.all([
            api.get('/merchants'),
            api.get('/merchant-types')
          ]);
          setMerchants(merchantsResponse.data);
          setAvailableTypes(typesResponse.data);
        }
      } catch (err) {
        console.error('Erreur lors de la récupération des commerçants :', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [isSubSite, subSiteSlug]);

  // 2) Appliquer le filtre (types + search)
  useEffect(() => {
    let newFiltered = merchants;

    // Filtre par types (multi-sélection)
    if (selectedTypes.length > 0) {
      newFiltered = newFiltered.filter((merchant) =>
        merchant.types.some(type => selectedTypes.includes(type.slug))
      );
    }

    // Filtre par recherche
    if (searchQuery.trim()) {
      const lowerSearch = searchQuery.toLowerCase();
      newFiltered = newFiltered.filter((merchant) => {
        const nameMatch = merchant.name.toLowerCase().includes(lowerSearch);
        const descriptionMatch = merchant.description
          ? merchant.description.toLowerCase().includes(lowerSearch)
          : false;
        const locationMatch = merchant.location
          ? merchant.location.toLowerCase().includes(lowerSearch)
          : false;
        return nameMatch || descriptionMatch || locationMatch;
      });
    }

    setFilteredItems(newFiltered);
    setDisplayCount(12); // reset pagination quand on change de filtre
  }, [merchants, selectedTypes, searchQuery]);

  // 3) Handlers
  const handleTypeToggle = (typeSlug) => {
    setSelectedTypes(prev =>
      prev.includes(typeSlug)
        ? prev.filter(slug => slug !== typeSlug)
        : [...prev, typeSlug]
    );
  };

  const handleSearchChange = (e) => setSearchQuery(e.target.value);

  const resetFilters = () => {
    setSelectedTypes([]);
    setSearchQuery('');
  };

  const loadMore = () => {
    setDisplayCount((prev) => prev + 9);
  };

  // Check loading / error
  if (settingsLoading) return <div><Spinner /></div>;
  if (error) return <div>Erreur : {error}</div>;

  const itemsToDisplay = filteredItems.slice(0, displayCount);

  return (
    <>
      <Helmet>
        <title>Arras | Commerçants - Ville de Noël</title>
        <meta name="description" content="Découvrez tous les commerçants du marché de Noël d'Arras : gauffres, marrons chauds, artisanat, décorations et bien plus." />
        <meta property="og:title" content="Arras | Commerçants - Ville de Noël" />
        <meta property="og:description" content="Retrouvez tous les commerçants du marché de Noël d'Arras. Saveurs, artisanat et produits festifs vous attendent !" />
        <meta property="og:image" content={`${designSettings.defaultAssoBanner || '/images/default-banner.jpg'}`} />
        <meta property="og:image:alt" content={`Image descriptive pour la page Commerçants`} />
        <link rel="canonical" href={`https://www.arras.fr${merchantsURL}`} />
      </Helmet>

      <div className={styles.pageWrapper}>
        <ArchivesHeader
          title="Commerçants de la Ville de Noël"
          imageUrl={designSettings.defaultAssoBanner || '/images/default-banner.jpg'}
        />

        <div className={`page-content ${styles.pageContent} ${styles.pageMerchants}`}>
          <section className={`global-section ${styles.headerSection}`}>
            <div className={`global-container ${styles.offsetHeader}`}>
              <MerchantsArchiveFilter
                selectedTypes={selectedTypes}
                handleTypeToggle={handleTypeToggle}
                searchQuery={searchQuery}
                handleSearchChange={handleSearchChange}
                resetFilters={resetFilters}
                availableTypes={availableTypes}
              />
            </div>
          </section>

          <section className={`global-section ${styles.contentSection}`}>
            <div className="global-container">
              <ToggleView view={view} setView={setView} />

              {loading ? (
                <div>Chargement des commerçants...</div>
              ) : itemsToDisplay && itemsToDisplay.length > 0 ? (
                view === 'grid' ? (
                  <ArchivesGridView
                    items={itemsToDisplay}
                    renderItem={(merchant) => (
                      <MerchantCard key={merchant.id} merchant={merchant} />
                    )}
                    displayCount={displayCount}
                  />
                ) : (
                  <ArchivesListView
                    items={itemsToDisplay}
                    renderItem={(merchant) => (
                      <ListItem
                        key={merchant.id}
                        title={merchant.name}
                        meta={[
                          merchant.location ? `📍 ${merchant.location}` : '',
                          merchant.contactEmail ? `Email : ${merchant.contactEmail}` : '',
                          merchant.contactPhone ? `Téléphone : ${merchant.contactPhone}` : '',
                        ].filter(Boolean)}
                        description={
                          merchant.description
                            ? merchant.description
                                .replace(/<\/?[^>]+(>|$)/g, '')
                                .slice(0, 150) + '...'
                            : 'Aucune description disponible.'
                        }
                        categories={merchant.types || []}
                        link={`${merchantsURL}/${merchant.slug}`}
                        buttonText="En savoir plus"
                      />
                    )}
                    displayCount={displayCount}
                  />
                )
              ) : (
                <p>Aucun commerçant trouvé.</p>
              )}

              {displayCount < filteredItems.length && (
                <LoadMoreButton onClick={loadMore}>
                  Charger plus
                </LoadMoreButton>
              )}
            </div>
          </section>

          <MapCta />
          <LiensPratiques />
        </div>
      </div>
    </>
  );
};

export default MerchantsPage;

